import { GoogleMap, PolygonF, CircleF, MarkerF, PolylineF, useJsApiLoader, DrawingManagerF } from "@react-google-maps/api";
import { useMemo, useCallback, useState, useEffect } from "react";
import { Button, Spinner } from "reactstrap";

const libraries: any = ['places', 'drawing', 'geometry'];

interface MapData {
    type: 'polygon' | 'circle';
    points?: { lat: number; lng: number }[];
    center?: { lat: number; lng: number };
    radius?: number;
}

export const RouteAreaMap = ({
  area,
  isOpen,
  isEdit = false,
  onSave,
  isSaving = false,
}: {
  area: any;
  isOpen: boolean;
  isEdit?: boolean;
  onSave?: (data: any) => void;
  isSaving?: boolean;
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>(null);
  const [tempArea, setTempArea] = useState<any>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    version: "3.64",
    libraries
  });

  const finalLoaded = isLoaded || (typeof window !== 'undefined' && !!window.google?.maps);

  const mapData = useMemo<MapData | null>(() => {
    const currentArea = tempArea || area;
    if (!currentArea) return null;
    try {
        let coordinates = currentArea.coordinates || currentArea.center || currentArea;
        if (typeof coordinates === 'string') {
            coordinates = JSON.parse(coordinates);
        }
        
        const type = currentArea.type || (Array.isArray(coordinates) ? 'polygon' : 'circle');
        const radius = currentArea.radius ? parseFloat(currentArea.radius) : 500;

        if (type === 'polygon' && Array.isArray(coordinates)) {
            const points = coordinates.map((p: any) => ({
                lat: parseFloat(p.lat),
                lng: parseFloat(p.lng)
            })).filter((p: any) => !isNaN(p.lat) && !isNaN(p.lng));
            if (points.length === 0) return null;
            return { type: 'polygon', points };
        } else {
            const centerRaw = Array.isArray(coordinates) ? coordinates[0] : coordinates;
            const center = { 
                lat: parseFloat(centerRaw?.lat), 
                lng: parseFloat(centerRaw?.lng) 
            };
            if (isNaN(center.lat) || isNaN(center.lng)) return null;
            return { type: 'circle', center, radius };
        }
    } catch (e) {
        return null;
    }
  }, [area, tempArea]);

  const onMapLoad = useCallback((m: google.maps.Map) => {
    setMap(m);
  }, []);

  const onDrawingManagerLoad = useCallback((dm: google.maps.drawing.DrawingManager) => {
    setDrawingManager(dm);
  }, []);

  const onOverlayComplete = (e: any) => {
    if (e.type === 'polygon') {
      const path = e.overlay.getPath();
      const points = [];
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        points.push({ lat: point.lat(), lng: point.lng() });
      }
      setTempArea({ type: 'polygon', coordinates: points });
      e.overlay.setMap(null); // Remove the drawn overlay, we will render via PolygonF
    } else if (e.type === 'circle') {
      const center = e.overlay.getCenter();
      const radius = e.overlay.getRadius();
      setTempArea({ type: 'circle', coordinates: { lat: center.lat(), lng: center.lng() }, radius });
      e.overlay.setMap(null);
    }
  };

  const [polygonRef, setPolygonRef] = useState<google.maps.Polygon | null>(null);
  const [circleRef, setCircleRef] = useState<google.maps.Circle | null>(null);

  const onPolygonLoad = useCallback((polygon: google.maps.Polygon) => {
    setPolygonRef(polygon);
    const path = polygon.getPath();
    const updatePath = () => {
      const points = [];
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        points.push({ lat: point.lat(), lng: point.lng() });
      }
      setTempArea({ type: 'polygon', coordinates: points });
    };

    path.addListener('set_at', updatePath);
    path.addListener('insert_at', updatePath);
    path.addListener('remove_at', updatePath);
    polygon.addListener('dragend', updatePath);
  }, []);

  const onCircleLoad = useCallback((circle: google.maps.Circle) => {
    setCircleRef(circle);
    const updateCircle = () => {
      const center = circle.getCenter();
      const radius = circle.getRadius();
      if (center) {
        setTempArea({ 
          type: 'circle', 
          coordinates: { lat: center.lat(), lng: center.lng() }, 
          radius 
        });
      }
    };

    circle.addListener('center_changed', updateCircle);
    circle.addListener('radius_changed', updateCircle);
    circle.addListener('dragend', updateCircle);
  }, []);

  const handleFinalSave = () => {
    let finalData = tempArea;
    
    // If no tempArea (no listener triggered), try to get from refs
    if (!finalData) {
        if (polygonRef) {
            const path = polygonRef.getPath();
            const points = [];
            for (let i = 0; i < path.getLength(); i++) {
                const point = path.getAt(i);
                points.push({ lat: point.lat(), lng: point.lng() });
            }
            finalData = { type: 'polygon', coordinates: points };
        } else if (circleRef) {
            const center = circleRef.getCenter();
            const radius = circleRef.getRadius();
            if (center) {
                finalData = { 
                    type: 'circle', 
                    coordinates: { lat: center.lat(), lng: center.lng() }, 
                    radius 
                };
            }
        }
    }
    
    onSave?.(finalData || area);
  };

  useEffect(() => {
    if (map && mapData) {
      const bounds = new window.google.maps.LatLngBounds();
      if (mapData.type === 'polygon' && mapData.points) {
        mapData.points.forEach(p => bounds.extend(p));
      } else if (mapData.type === 'circle' && mapData.center) {
        const radiusInDegrees = (mapData.radius || 500) / 111320;
        bounds.extend({ lat: mapData.center.lat + radiusInDegrees, lng: mapData.center.lng + radiusInDegrees });
        bounds.extend({ lat: mapData.center.lat - radiusInDegrees, lng: mapData.center.lng - radiusInDegrees });
      }
      map.fitBounds(bounds);
    }
  }, [map, mapData]);

  if (loadError) return <div className="p-4 text-center text-danger">Maps Load Error: {loadError.message}</div>;

  if (!finalLoaded || !isOpen) {
      return (
          <div className="d-flex align-items-center justify-content-center h-100 bg-light border rounded">
              <div className="spinner-border text-primary" role="status"></div>
          </div>
      );
  }

  const initialCenter = mapData?.type === 'polygon' ? mapData.points?.[0] : mapData?.center || { lat: 11.0168, lng: 76.9558 };

  return (
    <div style={{ width: "100%", height: "100%", minHeight: "500px", position: "relative" }}>
      {isEdit && (
        <div style={{ 
          position: "absolute", 
          top: "10px", 
          right: "10px", 
          zIndex: 10,
          display: "flex",
          gap: "10px"
        }}>
          <Button 
            color="success" 
            size="sm" 
            onClick={handleFinalSave} 
            disabled={!mapData || isSaving}
          >
            {isSaving ? (
              <>
                <Spinner size="sm" className="me-1"> Loading... </Spinner>
                Saving...
              </>
            ) : "Save Location"}
          </Button>
          <Button color="secondary" size="sm" onClick={() => { setTempArea(null); }} disabled={isSaving}>
            Reset
          </Button>
        </div>
      )}
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%", minHeight: "500px" }}
        center={initialCenter}
        zoom={15}
        onLoad={onMapLoad}
        options={{ 
            streetViewControl: false, 
            mapTypeControl: true, 
            fullscreenControl: true,
            zoomControl: true 
        }}
      >
        {isEdit && (
          <DrawingManagerF
            onLoad={onDrawingManagerLoad}
            onOverlayComplete={onOverlayComplete}
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [
                  window.google.maps.drawing.OverlayType.POLYGON,
                  window.google.maps.drawing.OverlayType.CIRCLE,
                ],
              },
              polygonOptions: {
                fillColor: "#2ae",
                fillOpacity: 0.4,
                strokeWeight: 2,
                clickable: true,
                editable: true,
                zIndex: 1,
              },
              circleOptions: {
                fillColor: "#2ae",
                fillOpacity: 0.4,
                strokeWeight: 2,
                clickable: true,
                editable: true,
                zIndex: 1,
              }
            }}
          />
        )}

        {mapData?.type === 'polygon' && mapData.points && (
          <>
            <PolygonF
              paths={[mapData.points]}
              onLoad={onPolygonLoad}
              options={{ 
                fillColor: "#2ae", 
                fillOpacity: 0.4, 
                strokeColor: "#2ae", 
                strokeWeight: 2,
                zIndex: 1,
                editable: isEdit,
                draggable: isEdit
              }}
            />
            {!isEdit && (
              <>
                <PolylineF
                  path={mapData.points}
                  options={{ 
                    strokeColor: "#000", 
                    strokeOpacity: 0.6, 
                    strokeWeight: 1,
                    zIndex: 2
                  }}
                />
                {mapData.points.map((p, i) => (
                  <MarkerF 
                      key={`marker-${i}`}
                      position={p} 
                      label={{ text: (i+1).toString(), color: "white", fontWeight: 'bold' }}
                  />
                ))}
              </>
            )}
          </>
        )}
        {mapData?.type === 'circle' && mapData.center && (
            <>
                <CircleF
                    center={mapData.center}
                    radius={mapData.radius || 500}
                    onLoad={onCircleLoad}
                    options={{ 
                        fillColor: "#2ae", 
                        fillOpacity: 0.4, 
                        strokeColor: "#2ae", 
                        strokeWeight: 2,
                        zIndex: 1,
                        editable: isEdit,
                        draggable: isEdit
                    }}
                />
                {!isEdit && (
                  <MarkerF 
                      position={mapData.center} 
                      label="C"
                  />
                )}
            </>
        )}
      </GoogleMap>
    </div>
  );
};
